import { useReadAccess } from 'src/app/AppAuth/useReadAccess'
import { useCallback, useContext, useMemo } from 'react'
import { ChildReportingServicePromiseClient } from '@trustero/trustero-api-web/lib/childreporting/childreporting_grpc_web_pb'
import {
  ControlExportSnapshot,
  GetChildControlExportsResponse,
} from '@trustero/trustero-api-web/lib/childreporting/childreporting_pb'
import { Empty } from 'google-protobuf/google/protobuf/empty_pb'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { useHasChildAccountReporting } from 'src/context/Gatekeeper'
import { AuditContext } from 'src/context/AuditContext'
import { formatTimestampFromNumber } from 'src/Utils/formatDate'
import { DATE_FORMATS } from 'src/Utils/dateConstants'
import { useAuthorizedGrpcClient } from 'src/adapter/grpcClient'
import { NewGrpcResponse } from '../../hooks/types'
import { useSwrImmutableGrpc } from '../../useSwrImmutableGrpc'

/**
 * The three gates on showing a child's rows, none of which implies the others: the account holds the
 * child-reporting grant, the user may see other companies' data, and no audit is in scope.
 *
 * The audit gate is not a nicety — the parent/child pipeline carries continuous-compliance results
 * only, so a child's rows inside an audit export would silently be results from outside the audit
 * period.
 *
 * `auditId` is the audit the CALLER was given, and it has to be resolved the same way the controls
 * query resolves it: `auditId ?? currentAuditId`. The audit index hands its download an explicit id
 * as a prop and never enters the audit, so AuditContext is empty there — reading the context alone
 * let an audit-scoped export collect child rows anyway. Whatever scopes the controls has to scope
 * this, or the two drift apart exactly there.
 */
const useMayShowChildAccounts = (auditId?: string): boolean => {
  const { auditId: currentAuditId } = useContext(AuditContext)
  const hasChildReporting = useHasChildAccountReporting()
  const canViewChildAccounts = useReadAccess(CAPABILITY.CAN_VIEW_CHILD_ACCOUNT_DASHBOARDS)
  return (
    hasChildReporting && canViewChildAccounts && !(auditId ?? currentAuditId)
  )
}

/**
 * Fetches the child snapshots at the moment a download is confirmed.
 *
 * Not the SWR hook below, and the difference is a disclosure one rather than a freshness one. That
 * hook revalidates on mount and nothing else — focus and reconnect revalidation are both off — so a
 * tab parked on the dashboard would keep serving whatever was current when the page loaded, however
 * many exports are taken from it. A child that revokes in the meantime would keep appearing in every
 * one of them, because the server gates that reject its snapshot are never asked again.
 *
 * It also matches what this file's other downloads already do: controls and policies are fetched in
 * the callback so the file reflects the moment it was asked for, and the children have no business
 * being the one part of it that dates from page load.
 *
 * Returns nothing for an account that may not show child rows, so the caller needs no second gate.
 */
export const useFetchChildControlExports = (
  auditId?: string,
): (() => Promise<ControlExportSnapshot.AsObject[]>) => {
  const mayShow = useMayShowChildAccounts(auditId)
  const client = useAuthorizedGrpcClient(ChildReportingServicePromiseClient)

  return useCallback(async () => {
    if (!mayShow) return []
    const response = await client.getChildControlExports(new Empty())
    return response.getSnapshotsList().map((snapshot) => snapshot.toObject())
  }, [client, mayShow])
}

/**
 * The control export snapshots this parent account holds for its consented children.
 *
 * For DISPLAY only — the count and "as of" date in the download confirmation. The file itself is
 * built from useFetchChildControlExports, which re-asks the server when the download is confirmed;
 * this may lag behind it by however long the tab has been open, and a label that lags is a different
 * thing from a file that does.
 *
 * Empty for almost every account, which is the normal answer rather than a failure.
 */
export const useChildControlExports = (
  shouldFetch = true,
  auditId?: string,
): {
  snapshots: ControlExportSnapshot.AsObject[]
  isLoading: boolean
} => {
  const mayShow = useMayShowChildAccounts(auditId)
  const enabled = shouldFetch && mayShow

  const { response } = useSwrImmutableGrpc(
    ChildReportingServicePromiseClient.prototype.getChildControlExports,
    new Empty(),
    enabled,
  )
  const { data, isLoading } =
    NewGrpcResponse<GetChildControlExportsResponse>(response)

  return useMemo(
    () => ({
      snapshots:
        data?.getSnapshotsList().map((snapshot) => snapshot.toObject()) ?? [],
      isLoading: Boolean(enabled && isLoading),
    }),
    [data, enabled, isLoading],
  )
}

/**
 * The oldest report date among the snapshots, which is how current the WHOLE merged export is.
 *
 * The oldest rather than the newest: a download is as stale as the most out-of-date thing in it,
 * and a child that has stopped publishing is exactly what a reader needs to be told about.
 */
export const oldestSnapshotDate = (
  snapshots: ControlExportSnapshot.AsObject[],
): string =>
  snapshots.reduce(
    (oldest, snapshot) =>
      !oldest || snapshot.reportDate < oldest ? snapshot.reportDate : oldest,
    '',
  )

/**
 * Renders a snapshot's report date for display.
 *
 * UTC throughout, and not a detail. A report date is a UTC day with no time in it, and `Date.parse`
 * of a date-only string yields UTC midnight — so formatting it locally moves it to the previous
 * calendar day for every user west of Greenwich. The date shown would then disagree with the one the
 * child published, by a day, for most of the Americas.
 */
export const formatSnapshotDate = (reportDate: string): string =>
  reportDate
    ? formatTimestampFromNumber(
        Date.parse(reportDate),
        DATE_FORMATS.SHORT,
        true,
      )
    : ''
